'use client';

import { InventaireResponse } from '@repo/types';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Label } from '@repo/ui/components/ui/label';
import { Calculator } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AESTHETIC_INDEX_TAB, AESTHETIC_INDEX_VALUE, HEALTH_INDEX_TAB, HEALTH_INDEX_VALUE, LOCATION_INDEX_TAB } from '../../../../../lib/inventory/constant/beva.const';
import { useCalculateBevaInventaire } from '../../../hooks/use-beva';
import { FormData } from '../../../shared/types';

interface BevaDetailsProps {
  formData: FormData;
  originalData: InventaireResponse | null;
  inventaireId: number;
}

// Convert constants to select options
const HEALTH_OPTIONS = HEALTH_INDEX_TAB.map(item => ({
  value: item.name.toString(),
  label: item.displayName
}));

const AESTHETIC_OPTIONS = AESTHETIC_INDEX_TAB.map(item => ({
  value: item.name.toString(),
  label: item.displayName
}));

const LOCATION_OPTIONS = LOCATION_INDEX_TAB.map(item => ({
  value: item.name.toString(),
  label: item.displayName
}));

// Health matrix structure based on the constants
const HEALTH_MATRIX = {
  rows: ['Vigoureux', 'Vigueur moyenne', 'Peu vigoureux', 'Sans vigueur'],
  cols: ['Sain', 'Malade', 'Dépérissant'],
  cells: [
    // Vigoureux row
    [
      { id: 1, label: 'Sain - Vigoureux', available: true },
      { id: 5, label: 'Malade - Vigoureux', available: true },
      { id: null, label: '', available: false }
    ],
    // Vigueur moyenne row
    [
      { id: 2, label: 'Sain - Vigueur moyenne', available: true },
      { id: 6, label: 'Malade - Vigueur moyenne', available: true },
      { id: null, label: '', available: false }
    ],
    // Peu vigoureux row
    [
      { id: 3, label: 'Sain - Peu vigoureux', available: true },
      { id: 7, label: 'Malade - Peu vigoureux', available: true },
      { id: 11, label: 'Dépérissant - Peu vigoureux', available: true }
    ],
    // Sans vigueur row
    [
      { id: 4, label: 'Sain - Sans vigueur', available: true },
      { id: 8, label: 'Malade - Sans Vigueur', available: true },
      { id: 12, label: 'Dépérissant - Sans vigueur', available: true }
    ]
  ]
};

// Aesthetic matrix structure
const AESTHETIC_MATRIX = {
  rows: ['Solitaire', 'Groupe < 5', 'Alignement ou groupe > 5'],
  cols: ['Remarquable', 'Beau sujet', 'Mal formé, âgé', 'Sans intérêt'],
  cells: [
    // Solitaire row
    [
      { id: 1, label: 'Solitaire - Remarquable', available: true },
      { id: 2, label: 'Solitaire - Beau sujet', available: true },
      { id: 3, label: 'Solitaire - Mal formé, âgé', available: true },
      { id: 4, label: 'Solitaire - Sans intérêt', available: true }
    ],
    // Groupe < 5 row
    [
      { id: 5, label: 'Groupe < 5 - Remarquable', available: true },
      { id: 6, label: 'Groupe < 5 - Beau sujet', available: true },
      { id: 7, label: 'Groupe < 5 - Mal formé, âgé', available: true },
      { id: 8, label: 'Groupe < 5 - Sans intérêt', available: true }
    ],
    // Alignement ou groupe > 5 row
    [
      { id: null, label: '', available: false },
      { id: 10, label: 'Alignement groupe > 5 - Beau sujet', available: true },
      { id: 11, label: 'Alignement groupe > 5 - Mal formé, âgé', available: true },
      { id: 12, label: 'Alignement groupe > 5 - Sans intérêt', available: true }
    ]
  ]
};

const LOCATION_INDEX_RECORD: any = {
  10: "Centre ville",
  8: "Agglomération",
  6: "Zone rurale"
}


interface MatrixProps {
  title: string;
  matrix: typeof HEALTH_MATRIX;
  selectedId: number | string | null;
  isReadOnly?: boolean;
}

function Matrix({ title, matrix, selectedId, isReadOnly = true }: MatrixProps) {
  const getGridColsClass = (colCount: number) => {
    switch (colCount) {
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-4';
      default: return 'grid-cols-3';
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {title}
      </Label>
      <div className="border rounded-lg overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-[150px_1fr] bg-gray-50">
          <div className="p-3 border-r font-medium text-sm">{title}</div>
          <div className={`grid ${getGridColsClass(matrix.cols.length)} divide-x`}>
            {matrix.cols.map((col, index) => (
              <div key={index} className="p-3 text-center font-medium text-sm">
                {col}
              </div>
            ))}
          </div>
        </div>

        {/* Matrix rows */}
        {matrix.rows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-[150px_1fr] border-t">
            <div className="p-3 border-r font-medium text-sm bg-gray-50">
              {row}
            </div>
            <div className={`grid ${getGridColsClass(matrix.cols.length)} divide-x`}>
              {(matrix.cells[rowIndex] || []).map((cell, colIndex) => (
                <div key={colIndex} className="p-1">
                  {cell.available ? (
                    <div
                      className={`w-full h-16 flex items-center justify-center text-xs p-2 ${selectedId === cell.id
                        ? "bg-green-500 text-white font-medium"
                        : "bg-gray-100 text-gray-500"
                        }`}
                    >
                      {selectedId === cell.id ? '✓' : ''}
                    </div>
                  ) : (
                    <div className="w-full h-16 bg-gray-100"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BevaDetails({ formData, originalData, inventaireId }: BevaDetailsProps) {
  const [calculatedBeva, setCalculatedBeva] = useState<number | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const calculateBevaMutation = useCalculateBevaInventaire();

  // Get values from form data using the correct field names that match the backend
  const varietyGrade = formData['valeur-intrinseque-indice-espece'] || '';
  const healthColumn = formData['health_column'] || '';
  const aestheticColumn = formData['aesthetic_column'] || '';
  const locationIndex = formData['location_index'] ? formData['location_index'].toString() : '';

  // Calculate BEVA values based on selected indices
  const calculatedValues = {
    varietyGrade: parseFloat(varietyGrade) || 0,
    healthIndex: (() => {
      const healthItem = HEALTH_INDEX_VALUE.find(item => item.column === parseInt(healthColumn));
      return healthItem ? healthItem.value : 0;
    })(),
    aestheticIndex: (() => {
      const aestheticItem = AESTHETIC_INDEX_VALUE.find(item => item.column === parseInt(aestheticColumn));
      return aestheticItem ? aestheticItem.value : 0;
    })(),
    locationIndex: parseFloat(locationIndex) || 0
  };

  // Function to calculate BEVA on backend
  const calculateBevaOnBackend = async () => {
    if (!varietyGrade || !healthColumn || !aestheticColumn || !locationIndex || !inventaireId) {
      console.warn('Missing required data for BEVA calculation:', {
        varietyGrade,
        healthColumn,
        aestheticColumn,
        locationIndex,
        inventaireId
      });
      return;
    }

    try {
      const bevaData = {
        varietyGrade: parseFloat(varietyGrade),
        healthIndex: calculatedValues.healthIndex,
        aestheticIndex: calculatedValues.aestheticIndex,
        locationIndex: calculatedValues.locationIndex,
        healthColumn: parseInt(healthColumn),
        aestheticColumn: parseInt(aestheticColumn)
      };

      const result = await calculateBevaMutation.mutateAsync({
        inventaireId,
        data: bevaData
      });

      // Set the calculated BEVA value from the backend
      //@ts-ignore
      setCalculatedBeva(result.data.beva);

      // Show success message (you could add toast notification here)
      //@ts-ignore
      console.log('BEVA calculated successfully:', result.data);

    } catch (error) {
      console.error('Error calculating BEVA:', error);
      // You could add error toast notification here
    }
  };

  // Auto-calculate BEVA when component mounts and all required data is available
  useEffect(() => {
    const hasAllRequiredData = varietyGrade && healthColumn && aestheticColumn && locationIndex && inventaireId;

    if (hasAllRequiredData && calculatedBeva === null && !isInitialLoading) {
      setIsInitialLoading(true);
      calculateBevaOnBackend().finally(() => {
        setIsInitialLoading(false);
      });
    }
  }, [varietyGrade, healthColumn, aestheticColumn, locationIndex, inventaireId, calculatedBeva, isInitialLoading]);

  return (
    <div className="space-y-6">
      {/* Variety Grade (Species Index) */}
      {/* <div className="space-y-2">
        <Label className="text-sm font-medium">
          Note variétale (Indice espèce)
        </Label>
        <div className="p-3 bg-gray-50 rounded-md border">
          <span className="text-sm font-medium">{calculatedValues.varietyGrade}</span>
        </div>
      </div> */}

      {/* Health Index Matrix */}
      <Matrix
        title="Indice sanitaire"
        matrix={HEALTH_MATRIX}
        selectedId={healthColumn ? parseInt(healthColumn) : null}
        isReadOnly={true}
      />

      {/* Aesthetic Index Matrix */}
      <Matrix
        title="Indice esthétique"
        matrix={AESTHETIC_MATRIX}
        selectedId={aestheticColumn ? parseInt(aestheticColumn) : null}
        isReadOnly={true}
      />

      {/* Location Index */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          Indice de situation
        </Label>
        <div className="p-3 bg-gray-50 rounded-md border">
          <span className="text-sm font-medium">{LOCATION_INDEX_RECORD?.[calculatedValues.locationIndex as any]}</span>
        </div>
      </div>

      {/* Calculate BEVA Button */}
      <div className="space-y-2">
        <Button
          onClick={calculateBevaOnBackend}
          disabled={calculateBevaMutation.isPending || isInitialLoading || !varietyGrade || !healthColumn || !aestheticColumn || !locationIndex || !inventaireId}
          className="w-full flex items-center justify-center space-x-2"
        >
          {/* @ts-ignore */}
          <Calculator className="w-4 h-4" />
          <span>
            {calculateBevaMutation.isPending || isInitialLoading ? 'Calcul en cours...' : 'Recalculer la valeur BEVA'}
          </span>
        </Button>
        <p className="text-xs text-gray-500 text-center">
          Le calcul BEVA prend en compte le diamètre de l'arbre pour déterminer l'indice de taille
        </p>
        {calculateBevaMutation.isError && (
          <p className="text-xs text-red-600 text-center">
            Erreur lors du calcul BEVA. Veuillez réessayer.
          </p>
        )}
      </div>

      {/* Calculated Values Display */}
      <Card className="bg-gray-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Valeurs calculées</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-600">Note variétale</Label>
              <div className="text-sm font-medium">{calculatedValues.varietyGrade}</div>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Indice sanitaire (valeur)</Label>
              <div className="text-sm font-medium">{calculatedValues.healthIndex}</div>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Indice esthétique (valeur)</Label>
              <div className="text-sm font-medium">{calculatedValues.aestheticIndex}</div>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Indice de situation</Label>
              <div className="text-sm font-medium">{calculatedValues.locationIndex}</div>
            </div>
          </div>

          <div className="pt-3 border-t">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium text-gray-700">Valeur BEVA</Label>
              <div className="text-lg font-bold text-green-600">
                {isInitialLoading || calculateBevaMutation.isPending ?
                  'Calcul en cours...' :
                  calculatedBeva !== null ? `${calculatedBeva.toFixed(2)} €` : 'Non calculée'
                }
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {calculatedBeva !== null ?
                'Formule: (Esthétique + Sanitaire) × (Variétale × Situation) × Taille × Nombre' :
                isInitialLoading || calculateBevaMutation.isPending ?
                  'Calcul automatique en cours...' :
                  'Cliquez sur "Recalculer la valeur BEVA" pour obtenir le résultat'
              }
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start space-x-2">
            <div className="text-blue-600 mt-0.5">ℹ️</div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Information sur l'évaluation BEVA</p>
              <p>
                La méthode BEVA (Barème d'Évaluation de la Valeur des Arbres) permet d'estimer
                la valeur patrimoniale d'un arbre en tenant compte de son espèce, de son état
                sanitaire, de ses qualités esthétiques et de sa situation géographique.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
